/**
 * Centralized language utilities for iLing
 * This file contains all language-to-country code mappings to ensure consistency
 */

// Country code mapping for flags - maps language codes and names to ISO country codes
export const languageCountryCodes = {
  // Spanish
  'es': 'es', 'Spanish': 'es', 'spa': 'es',
  // French
  'fr': 'fr', 'French': 'fr', 'fra': 'fr',
  // German
  'de': 'de', 'German': 'de', 'deu': 'de',
  // Chinese
  'zh': 'cn', 'Mandarin Chinese': 'cn', 'Chinese': 'cn', 'zho': 'cn',
  // Japanese
  'ja': 'jp', 'Japanese': 'jp', 'jpn': 'jp',
  // Korean
  'ko': 'kr', 'Korean': 'kr', 'kor': 'kr',
  // Arabic
  'ar': 'sa', 'Arabic': 'sa', 'ara': 'sa',
  // Russian
  'ru': 'ru', 'Russian': 'ru', 'rus': 'ru',
  // Portuguese
  'pt': 'br', 'Portuguese': 'br', 'por': 'br',
  // Persian
  'fa': 'ir', 'Persian': 'ir', 'Persian (Farsi)': 'ir', 'fas': 'ir', 'per': 'ir',
  // Italian
  'it': 'it', 'Italian': 'it', 'ita': 'it',
  // Hindi
  'hi': 'in', 'Hindi': 'in', 'hin': 'in',
  // Turkish
  'tr': 'tr', 'Turkish': 'tr', 'tur': 'tr',
  // Dutch
  'nl': 'nl', 'Dutch': 'nl', 'nld': 'nl',
  // Polish
  'pl': 'pl', 'Polish': 'pl', 'pol': 'pl',
  // Vietnamese
  'vi': 'vn', 'Vietnamese': 'vn', 'vie': 'vn',
  // Thai
  'th': 'th', 'Thai': 'th', 'tha': 'th',
  // English
  'en': 'gb', 'English': 'gb', 'eng': 'gb',
  // Afrikaans
  'af': 'za', 'Afrikaans': 'za', 'afr': 'za',
  // Hebrew
  'he': 'il', 'Hebrew': 'il', 'heb': 'il',
  // Ukrainian
  'uk': 'ua', 'Ukrainian': 'ua', 'ukr': 'ua',
  // Greek
  'el': 'gr', 'Greek': 'gr', 'ell': 'gr',
  // Czech
  'cs': 'cz', 'Czech': 'cz', 'ces': 'cz',
  // Swedish
  'sv': 'se', 'Swedish': 'se', 'swe': 'se',
  // Norwegian
  'no': 'no', 'Norwegian': 'no', 'nor': 'no', 'nb': 'no', 'nn': 'no',
  // Danish
  'da': 'dk', 'Danish': 'dk', 'dan': 'dk',
  // Finnish
  'fi': 'fi', 'Finnish': 'fi', 'fin': 'fi',
  // Hungarian
  'hu': 'hu', 'Hungarian': 'hu', 'hun': 'hu',
  // Romanian
  'ro': 'ro', 'Romanian': 'ro', 'ron': 'ro',
  // Indonesian
  'id': 'id', 'Indonesian': 'id', 'ind': 'id',
  // Malay
  'ms': 'my', 'Malay': 'my', 'msa': 'my',
  // Swahili
  'sw': 'ke', 'Swahili': 'ke', 'swa': 'ke',
  // Armenian
  'hy': 'am', 'Armenian': 'am', 'hye': 'am',
  // Pashto
  'ps': 'af', 'Pashto': 'af', 'pus': 'af',
  // Xhosa
  'xh': 'za', 'Xhosa': 'za', 'xho': 'za',
  // Bengali
  'bn': 'bd', 'Bengali': 'bd', 'ben': 'bd',
  // Tamil
  'ta': 'in', 'Tamil': 'in', 'tam': 'in',
  // Urdu
  'ur': 'pk', 'Urdu': 'pk', 'urd': 'pk',
  // Igbo
  'ig': 'ng', 'Igbo': 'ng', 'ibo': 'ng',
  // Yoruba
  'yo': 'ng', 'Yoruba': 'ng', 'yor': 'ng',
  // Hausa
  'ha': 'ng', 'Hausa': 'ng', 'hau': 'ng',
  // Amharic
  'am': 'et', 'Amharic': 'et', 'amh': 'et',
  // Punjabi - uses India flag (also spoken in Pakistan)
  'pa': 'in', 'Punjabi': 'in', 'pan': 'in',
  // Syriac/Syrian - uses Syria flag (ancient language, closest modern country)
  'syr': 'sy', 'Syriac': 'sy', 'Syrian': 'sy',
  // Tibetan - uses China flag (Tibet region)
  'bo': 'cn', 'Tibetan': 'cn', 'bod': 'cn', 'tib': 'cn',
  // Biblical Hebrew
  'hbo': 'il', 'Biblical Hebrew': 'il',
  // Biblical Greek
  'grc': 'gr', 'Biblical Greek': 'gr',
  // Kriolu (Cape Verdean Creole)
  'kea': 'cv', 'Kriolu': 'cv', 'Cape Verdean Creole': 'cv',
};

/**
 * Get the country code for a language object
 * @param {Object} lang - Language object with code and/or name properties
 * @returns {string|null} - ISO country code or null if not found
 */
export const getCountryCode = (lang) => {
  if (!lang) return null;
  return languageCountryCodes[lang.code] || languageCountryCodes[lang.name] || null;
};

/**
 * Get the country code from a language code string
 * @param {string} code - Language code (e.g., 'en', 'ja', 'he')
 * @returns {string|null} - ISO country code or null if not found
 */
export const getCountryCodeFromString = (code) => {
  if (!code) return null;
  return languageCountryCodes[code] || null;
};
